// The mail server's JMAP (RFC 8620) with the person's mail token: its session (the API address and
// the account), then method calls. Used by two-step sign-in and app passwords through the mail
// server's own extension (27-admin.md, 21-user-settings.md); Mail joins later.
import { mailAccess, mailToken } from './mail';

const USING = ['urn:ietf:params:jmap:core', 'urn:stalwart:jmap'];
const sessions = new Map<string, { apiUrl: string; accountId: string }>();

async function send(userId: string, url: string, body?: unknown) {
  const go = async (refused: boolean) =>
    fetch(url, {
      method: body ? 'POST' : 'GET',
      credentials: 'omit',
      cache: 'no-store',
      headers: { Authorization: `Bearer ${await mailToken(userId, refused)}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
  let answer = await go(false);
  if (answer.status === 401) answer = await go(true);
  if (!answer.ok) throw Object.assign(new Error(`jmap ${answer.status}`), { status: answer.status });
  return answer.json();
}

/** One method call; its answer's arguments, or the method's error thrown with its `type`. */
export async function jmap<T = Record<string, any>>(userId: string, method: string, args: Record<string, unknown>): Promise<T> {
  let session = sessions.get(userId);
  if (!session) {
    const base = mailAccess(userId)!.base.replace(/\/+$/, '');
    // Asked once per account; only the paths of its addresses are used, after the mail address (23).
    const s = await send(userId, `${base}/jmap/session`);
    session = { apiUrl: base + new URL(s.apiUrl, base).pathname, accountId: s.primaryAccounts?.['urn:ietf:params:jmap:core'] ?? Object.keys(s.accounts ?? {})[0] };
    sessions.set(userId, session);
  }
  const answer = await send(userId, session.apiUrl, { using: USING, methodCalls: [[method, { accountId: session.accountId, ...args }, 'a']] });
  const [name, result] = answer.methodResponses?.[0] ?? [];
  if (name === 'error') throw Object.assign(new Error(result?.type ?? 'jmap'), { code: result?.type });
  return result as T;
}
